import { useState, useEffect, useCallback } from 'react'
import { message } from 'antd'
import Handlebars from 'handlebars'

import { useCaseViewContext } from '../context/CaseViewContext'

export const useCharting = ({ apiClasses, apiType = 'physician' }) => {
  
  const { 
    chartingPrescriptions, 
    caseDetails, 
    activeRequest, 
    patient, 
  } = useCaseViewContext()
  
  const isReadOnly = apiType === 'admin'

  const [loading, setLoading] = useState(false)
  const [templates, setTemplates] = useState([])
  const [chartingEntries, setChartingEntries] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selectedChartingPrescription, setSelectedChartingPrescription] = useState(null)
  const [rawEntry, setRawEntry] = useState('')
  const [compiledEntry, setCompiledEntry] = useState('')

  const getChartingTemplates = useCallback(async () => {

    if (!apiClasses?.TemplatesAPI) {
      throw new Error('CaseTagsAPI not provided');
    }

    if (!caseDetails?.category?._id) return

    try {
      setLoading(true)
      
      const { data } = await apiClasses.TemplatesAPI.getMany({ 
        type: 'visit',
        teleMedicineCategoryIds: caseDetails.category._id
      })
      
      const { templates } = data
      
      if (Array.isArray(templates)) {
        setTemplates(templates)
      } else {
        setTemplates([])
      }
    } catch (error) {
      console.error('Error fetching charting templates:', error)
      message.error('Failed to load charting templates')
    } finally {
      setLoading(false)
    }
  }, [apiClasses, caseDetails?.category?._id])

  const addChartingEntry = useCallback(async (entryData) => {
    if (isReadOnly) {
      message.warning('Charting is read-only for admin users')
      return false
    }

    if (!apiClasses?.PhysicianAPI) {
      throw new Error('PhysicianAPI not provided');
    }
    
    if (!caseDetails || !activeRequest) return

    try {
      setLoading(true)
      
      const caseId = caseDetails.caseId || caseDetails._id
      const requestId = activeRequest._id
      
      const { data } = await apiClasses.PhysicianAPI.addChartingEntryToCaseReqest({
        caseId,
        requestId,
        note: entryData.note
      })

      const isSuccess = data.success || (data.message && data.message.toLowerCase().includes('successfully'))
      
      if (isSuccess) {
        message.success('Charting entry added successfully')
        
        const newEntry = data.entry ? {
          ...data.entry, 
          updatedAt: new Date().toISOString() 
        } : {
          _id: `temp_${Date.now()}`,
          note: entryData.note,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'current_user',
          requestId: activeRequest._id
        }
        
        setChartingEntries(prevEntries => [newEntry, ...prevEntries])
        return true
      } else {
        throw new Error(data?.message || 'Failed to add charting entry')
      }
    } catch (error) {
      console.error('Error adding charting entry:', error)
      console.error('Error details:', {
        caseId: caseDetails.caseId || caseDetails._id,
        requestId: activeRequest._id,
        error: error.response?.data || error.message
      })
      message.error(error.response?.data?.message || error.message || 'Failed to add charting entry')
      return false
    } finally {
      setLoading(false)
    }
  }, [apiClasses, caseDetails, activeRequest, isReadOnly])

  const editChartingEntry = useCallback(async (entryData) => {
    if (isReadOnly) {
      message.warning('Charting is read-only for admin users')
      return false
    }

    if (!apiClasses?.PhysicianAPI) {
      throw new Error('PhysicianAPI not provided');
    }
    
    if (!caseDetails || !activeRequest) return

    try {
      setLoading(true)
      
      const { data } = await apiClasses.PhysicianAPI.editRequestChartingEntry({
        caseId: caseDetails.caseId || caseDetails._id,
        requestId: activeRequest._id,
        entry: { _id: entryData._id, note: entryData.note }
      })

      const isSuccess = !data.error || (data.message && data.message.toLowerCase().includes('successfully'))

      if (isSuccess) {
        message.success('Charting entry updated successfully')
        
        setChartingEntries(prevEntries => {
          const updated = prevEntries.map(entry => {
            if (entry._id === entryData._id) {
              return { ...entry, note: entryData.note, updatedAt: new Date().toISOString() }
            }
            return entry
          })
          return updated
        })
        return true
      } else {
        throw new Error(data?.message || 'Failed to edit charting entry')
      }
    } catch (error) {
      console.error('Error editing charting entry:', error)
      console.error('Error details:', {
        caseId: caseDetails.caseId || caseDetails._id,
        requestId: activeRequest._id,
        entryId: entryData._id,
        error: error.response?.data || error.message
      })
      message.error(error.response?.data?.message || error.message || 'Failed to edit charting entry')
      return false
    } finally {
      setLoading(false)
    }
  }, [apiClasses, caseDetails, activeRequest, isReadOnly])

  const deleteChartingEntry = useCallback(async (entryId) => {
    if (isReadOnly) {
      message.warning('Charting is read-only for admin users')
      return false
    }

    if (!apiClasses?.PhysicianAPI) {
      throw new Error('PhysicianAPI not provided');
    }
    
    if (!caseDetails || !activeRequest) return

    try {
      setLoading(true)
      
      const { data } = await apiClasses.PhysicianAPI.deleteChartingEntryFromCaseReqest({
        caseId: caseDetails.caseId || caseDetails._id,
        requestId: activeRequest._id,
        entryId
      })

      const isSuccess = data.success || !data.error

      if (isSuccess) {
        message.success('Charting entry deleted successfully')
        
        setChartingEntries(prevEntries => {
          const updated = prevEntries.filter(entry => entry._id !== entryId)
          return updated
        })
        
        return true
      } else {
        throw new Error(data?.message || 'Failed to delete charting entry')
      }
    } catch (error) {
      console.error('Error deleting charting entry:', error)
      console.error('Error details:', {
        entryId,
        caseId: caseDetails.caseId || caseDetails._id,
        requestId: activeRequest._id,
        error: error.response?.data || error.message
      })
      message.error(error.response?.data?.message || error.message || 'Failed to delete charting entry')
      return false
    } finally {
      setLoading(false)
    }
  }, [apiClasses, caseDetails, activeRequest, isReadOnly])

  const aggregateChartingEntries = useCallback(() => {
    if (!caseDetails?.requests) {
      setChartingEntries([])
      return
    }

    const flattenedChartingEntries = caseDetails.requests.reduce((entries, request) => {
      return entries.concat(request.chartingEntries || [])
    }, [])

    const filteredAndSortedEntries = flattenedChartingEntries
      .filter(entry => !entry.deleted)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

    setChartingEntries(filteredAndSortedEntries)
  }, [caseDetails])

  const compileHandlebars = useCallback((template) => {
    try {
      const prescription = chartingPrescriptions[selectedChartingPrescription]
      const handlebarsTemplate = Handlebars.compile(template || '')
      return handlebarsTemplate({ patient, prescription })
    } catch (error) {
      console.error('Template compilation error:', error)
      return "Template contains errors. Please edit template."
    }
  }, [patient, chartingPrescriptions, selectedChartingPrescription])

  const handleTextChange = useCallback(({ target }) => {
    const newRawEntry = target.value
    setRawEntry(newRawEntry)
    setCompiledEntry(compileHandlebars(newRawEntry))
  }, [compileHandlebars])

  const selectTemplate = useCallback((templateMessage) => {
    setSelectedTemplate(templateMessage)
    setRawEntry(templateMessage)
    setCompiledEntry(compileHandlebars(templateMessage))
  }, [compileHandlebars])

  const selectPrescription = useCallback((prescriptionId) => {
    setSelectedChartingPrescription(prescriptionId)
    setCompiledEntry(compileHandlebars(rawEntry))
  }, [rawEntry, compileHandlebars])

  const resetChartingEntry = useCallback(() => {
    setSelectedTemplate(null)
    setRawEntry('')
    setCompiledEntry('')
    setSelectedChartingPrescription(null)
  }, [])

  useEffect(() => {
    if (caseDetails && apiClasses?.TemplatesAPI) {
      getChartingTemplates()
    }
  }, [caseDetails, getChartingTemplates, apiClasses])

  useEffect(() => {
    if (caseDetails && chartingEntries.length === 0) {
      aggregateChartingEntries()
    }
  }, [caseDetails])

  useEffect(() => {
    if (rawEntry) {
      setCompiledEntry(compileHandlebars(rawEntry))
    }
  }, [selectedChartingPrescription, compileHandlebars, rawEntry])

  return {
    loading,
    templates,
    chartingEntries,
    selectedTemplate,
    selectedChartingPrescription,
    rawEntry,
    compiledEntry,
    chartingPrescriptions,
    addChartingEntry,
    editChartingEntry,
    deleteChartingEntry,
    selectTemplate,
    selectPrescription,
    handleTextChange,
    resetChartingEntry,
    getChartingTemplates,
    aggregateChartingEntries,
  }
}
